import React from 'react';
import { 
  Tag, Gift, X, CheckCircle
} from 'lucide-react';

/**
 * DiscountSection Component
 * Handles rendering of discount options with predefined and custom discounts
 * Features:
 * - Predefined discounts (configured in settings)
 * - Custom discount (percentage or fixed amount) - NO company max limit
 * - Discount reason field (optional/required)
 * - Real-time discount calculation
 * - Visual discount summary with savings display
 * - Change tracking in edit mode
 * Extracted from SimplifiedReservationForm for better code organization
 */
const DiscountSection = ({
  reservationSettings,
  discountState,
  setDiscountState,
  calculatedAmount,
  addonCosts,
  editIndex,
  isSaving,
  uploadingDocuments
}) => {
  
  // Get enabled discounts from settings
  const getEnabledDiscounts = () => {
    return (reservationSettings.discountSettings?.predefinedDiscounts || []).filter(discount => discount.enabled);
  };

  // ✅ CHANGED: no company max limit (was 50% / $500) - the owner decides.
  // Only natural limits remain: max 100%, and never more than the booking amount.
  const calculateDiscountAmount = (baseAmount, discountType, discountValue) => {
    const numValue = parseFloat(discountValue) || 0;
    if (numValue <= 0 || baseAmount <= 0) return 0;
    
    if (discountType === 'percentage') {
      const percentage = Math.min(numValue, 100);
      return (baseAmount * percentage) / 100;
    } else {
      return Math.min(numValue, baseAmount);
    }
  };

  // Handle predefined discount selection
  const handleDiscountChange = (discountId) => {
    const discount = getEnabledDiscounts().find(d => d.id === parseInt(discountId));
    const baseAmount = calculatedAmount + addonCosts;
    
    if (discount) {
      const discountAmount = calculateDiscountAmount(baseAmount, discount.type, discount.value);
      setDiscountState(prev => ({
        ...prev,
        selectedDiscount: discount,
        customDiscountType: 'percentage',
        customDiscountValue: '',
        discountAmount: discountAmount,
        discountChanges: editIndex !== null && JSON.stringify(discount) !== JSON.stringify(prev.originalDiscount)
      }));
    } else {
      // No discount selected
      setDiscountState(prev => ({
        ...prev,
        selectedDiscount: null,
        discountAmount: 0,
        discountChanges: editIndex !== null && prev.originalDiscount !== null
      }));
    }
  };

  // Handle custom discount change
  const handleCustomDiscountChange = (type, value) => {
    const baseAmount = calculatedAmount + addonCosts;
    const discountAmount = calculateDiscountAmount(baseAmount, type, value);
    
    setDiscountState(prev => ({
      ...prev,
      selectedDiscount: null,
      customDiscountType: type,
      customDiscountValue: value,
      discountAmount: discountAmount,
      discountChanges: editIndex !== null && (type !== 'percentage' || value !== '')
    }));
  };

  // ✅ CHANGED: only rejects impossible values (negative, or more than 100%)
  const validateDiscountValue = (type, value) => {
    if (!value) return true;
    
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) return false;
    
    if (type === 'percentage') return numValue <= 100;
    return true;
  };

  // Handle discount reason change
  const handleDiscountReasonChange = (reason) => {
    setDiscountState(prev => ({
      ...prev,
      discountReason: reason
    }));
  };

  if (!reservationSettings.discountSettings?.enableDiscounts) return null;

  const enabledDiscounts = getEnabledDiscounts();
  const baseAmount = calculatedAmount + addonCosts;

  return (
    <div>
      {/* Section Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 flex items-center justify-center">
            <Tag size={16} className="text-white" />
          </div>
          <div>
            <h3 className="text-white font-medium">Discounts</h3>
            {/* ✅ CHANGED: "available discounts • Max 50% or $500" line removed */}
          </div>
        </div>
        {editIndex !== null && (
          <div className={`px-2 py-1 rounded-full text-xs ${
            discountState.discountChanges 
              ? 'bg-orange-900/30 text-orange-400 border border-orange-700' 
              : 'bg-green-900/30 text-green-400 border border-green-700'
          }`}>
            {discountState.discountChanges ? 'Changes' : 'No Changes'}
          </div>
        )}
      </div>

      {/* Discounts Grid */}
      <div className="bg-gray-700/30 border border-gray-600 rounded-lg p-4 space-y-4">
        
        {/* Predefined Discounts */}
        {enabledDiscounts.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Predefined Discounts
            </label>
            <div className="grid gap-2 max-h-40 overflow-y-auto">
              {/* No Discount Option */}
              <div 
                className={`cursor-pointer rounded-lg border p-3 transition-colors ${
                  !discountState.selectedDiscount 
                    ? 'border-orange-500 bg-orange-900/30' 
                    : 'border-gray-600 bg-gray-700/50'
                } hover:border-orange-400`}
                onClick={() => handleDiscountChange('')}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <X className="w-4 h-4 text-red-400 mr-2" />
                    <span className="text-white text-sm">No Discount</span>
                  </div>
                  {!discountState.selectedDiscount && (
                    <CheckCircle className="w-4 h-4 text-orange-500" />
                  )}
                </div>
              </div>
              
              {/* Predefined Discount Options */}
              {enabledDiscounts.map((discount) => {
                const isSelected = discountState.selectedDiscount?.id === discount.id;
                const discountAmount = calculateDiscountAmount(baseAmount, discount.type, discount.value);
                
                return (
                  <div
                    key={discount.id}
                    className={`cursor-pointer rounded-lg border p-3 transition-colors ${
                      isSelected
                        ? 'border-green-500 bg-green-900/30'
                        : 'border-gray-600 bg-gray-700/50'
                    } hover:border-green-400`}
                    onClick={() => handleDiscountChange(discount.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Gift className="w-4 h-4 text-green-500 mr-2" />
                        <div>
                          <div className="font-medium text-white text-sm">{discount.name}</div>
                          <div className="text-xs text-gray-400">{discount.description}</div>
                          <div className="text-sm font-medium text-green-400">
                            {discount.type === 'percentage' ? `${discount.value}%` : `$${discount.value}`} off
                            {baseAmount > 0 && ` • Saves $${discountAmount.toFixed(2)}`}
                          </div>
                        </div>
                      </div>
                      {isSelected && (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Custom Discount */}
        {reservationSettings.discountSettings?.allowCustomDiscounts && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Custom Discount
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Discount Type */}
              <div>
                <select
                  value={discountState.customDiscountType}
                  onChange={(e) => handleCustomDiscountChange(e.target.value, discountState.customDiscountValue)}
                  className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  disabled={isSaving || uploadingDocuments || discountState.selectedDiscount}
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount ($)</option>
                </select>
              </div>
              
              {/* Discount Value - ✅ CHANGED: only percentage has a max (100); fixed amount has no max */}
              <div>
                <input
                  type="number"
                  min="0"
                  max={discountState.customDiscountType === 'percentage' ? 100 : undefined}
                  step={discountState.customDiscountType === 'percentage' ? '0.1' : '1'}
                  value={discountState.customDiscountValue}
                  onChange={(e) => handleCustomDiscountChange(discountState.customDiscountType, e.target.value)}
                  placeholder={`Enter ${discountState.customDiscountType === 'percentage' ? 'percentage' : 'amount'}`}
                  className={`w-full p-2 bg-gray-700 border text-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm transition-colors ${
                    discountState.customDiscountValue && !validateDiscountValue(discountState.customDiscountType, discountState.customDiscountValue)
                      ? 'border-red-500 bg-red-900/20'
                      : 'border-gray-600'
                  }`}
                  disabled={isSaving || uploadingDocuments || discountState.selectedDiscount}
                />
                {discountState.customDiscountValue && !validateDiscountValue(discountState.customDiscountType, discountState.customDiscountValue) && (
                  <div className="text-red-400 text-xs mt-1">
                    {discountState.customDiscountType === 'percentage'
                      ? 'Must be between 0% and 100%'
                      : 'Amount cannot be negative'}
                  </div>
                )}
              </div>
              
              {/* Savings Display */}
              <div className="md:col-span-1">
                <div className="text-green-400 font-medium text-sm p-2 bg-green-900/20 rounded-lg border border-green-500/30">
                  {discountState.customDiscountValue && validateDiscountValue(discountState.customDiscountType, discountState.customDiscountValue)
                    ? `Saves $${discountState.discountAmount.toFixed(2)}`
                    : '$0.00'
                  }
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Discount Reason */}
        {(reservationSettings.discountSettings?.requireDiscountReason || reservationSettings.discountSettings?.allowCustomDiscounts) && 
         (discountState.selectedDiscount || discountState.customDiscountValue) && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Discount Reason {reservationSettings.discountSettings?.requireDiscountReason && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={discountState.discountReason || ''}
              onChange={(e) => handleDiscountReasonChange(e.target.value)}
              placeholder="Enter reason for discount"
              className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              disabled={isSaving || uploadingDocuments}
            />
          </div>
        )}

        {/* Discount Summary */}
        {discountState.discountAmount > 0 && (
          <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-green-300">Applied Discount:</span>
              <span className="text-green-400 font-bold">-${discountState.discountAmount.toFixed(2)}</span>
            </div>
            <div className="text-xs text-green-300">
              {discountState.selectedDiscount 
                ? discountState.selectedDiscount.name
                : `Custom ${discountState.customDiscountType} discount`
              }
              {discountState.discountReason && ` • ${discountState.discountReason}`}
            </div>
            {editIndex !== null && discountState.discountChanges && (
              <div className="mt-2 text-xs text-orange-400 bg-orange-900/20 px-2 py-1 rounded border border-orange-700">
                Changes will be saved when you update the reservation
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscountSection;